import { T, useLang } from "@/lib/i18n";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { sendContact } from "@/lib/api";
import { PROFILE, ASSETS } from "@/data/portfolio";
import { track } from "@/lib/analytics";
import { Mail, Linkedin, ArrowUpRight } from "lucide-react";

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("Please fill in name, email and message."));
      return;
    }
    setLoading(true);
    try {
      await sendContact(form);
      track("contact_submit");
      toast.success(t("Message sent, Sinthu will reply shortly."));
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error(t("Couldn't send. Please try again or email directly."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="border-t border-[#e5e0d8] section-pad bg-[#faf8f5]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="font-mono-label text-[11px] text-[#9a7218]">
              {t("09 · Contact me")}
            </div>
            <p className="mt-4 text-base text-[#55625b] leading-relaxed">
              {t("If your digitalisation is data-driven and iterative and you need continuous data from your target audience, that’s exactly the job I want to own. This form or email, I reply within a working day.")}
            </p>

            <div className="mt-6 space-y-3">
              <a
                data-testid="contact-email-link"
                href={`mailto:${PROFILE.email}`}
                className="flex items-center gap-3 group text-[#14352a]"
              >
                <span className="w-9 h-9 grid place-items-center border border-[#e5e0d8] bg-white">
                  <Mail className="h-4 w-4 text-[#9a7218]" />
                </span>
                <span className="link-underline">{PROFILE.email}</span>
              </a>
              <a
                data-testid="contact-linkedin-link"
                href={PROFILE.linkedin}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 group text-[#14352a]"
              >
                <span className="w-9 h-9 grid place-items-center border border-[#e5e0d8] bg-white">
                  <Linkedin className="h-4 w-4 text-[#9a7218]" />
                </span>
                <span className="link-underline">linkedin.com/in/sinthunathan</span>
              </a>
              <a
                data-testid="contact-cv-link"
                href={ASSETS.cvPdf}
                target="_blank"
                rel="noreferrer"
                onClick={() => track("cv_download", { from: "contact" })}
                className="flex items-center gap-3 group text-[#14352a]"
              >
                <span className="w-9 h-9 grid place-items-center border border-[#e5e0d8] bg-white">
                  <ArrowUpRight className="h-4 w-4 text-[#9a7218]" />
                </span>
                <span className="link-underline">{t("Download CV (PDF)")}</span>
              </a>
            </div>
          </div>

          <form
            data-testid="contact-form"
            onSubmit={onSubmit}
            noValidate
            className="lg:col-span-7 p-6 sm:p-8 bg-white border border-[#e5e0d8] space-y-4"
          >
            <div>
              <Label htmlFor="name" className="font-mono-label text-[10px] text-[#86928a]">
                {t("Your name")}
              </Label>
              <Input
                data-testid="contact-name-input"
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-2 h-11 border-[#e5e0d8] focus-visible:ring-[#9a7218]"
                required
              />
            </div>
            <div>
              <Label htmlFor="email" className="font-mono-label text-[10px] text-[#86928a]">
                {t("Email")}
              </Label>
              <Input
                data-testid="contact-email-input"
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-2 h-11 border-[#e5e0d8] focus-visible:ring-[#9a7218]"
                required
              />
            </div>
            <div>
              <Label htmlFor="message" className="font-mono-label text-[10px] text-[#86928a]">
                {t("Message")}
              </Label>
              <Textarea
                data-testid="contact-message-input"
                id="message"
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="mt-2 border-[#e5e0d8] focus-visible:ring-[#9a7218]"
                required
              />
            </div>
            <Button
              data-testid="contact-submit-btn"
              type="submit"
              disabled={loading}
              className="btn-accent rounded-full h-11 px-8 text-sm font-medium"
            >
              {loading ? t("Sending…") : t("Send message")}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
}
